import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

export const fetchProductsWithFilter = createAsyncThunk(
  "products/fetchProductsWithFilter",
  async (filters, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();

      // backend says "All keys of filters are provided in params"
      if (filters?.category) params.set("category", filters.category);

      // If you want "inStock=true" like your example:
      // We'll treat inStock=true => stock > 0 (backend handles it)
      if (typeof filters?.inStock === "boolean") {
        params.set("inStock", String(filters.inStock));
      }

      params.set("page", String(filters?.page ?? 1));
      params.set("limit", String(filters?.limit ?? 100));

      const url =
        "https://3d8d71qhr3.execute-api.ap-south-1.amazonaws.com/Prod/Product/GetAllProductsWithFilter" +
        `?${params.toString()}`;

      const res = await fetch(url, { method: "GET" });

      if (!res.ok) {
        let msg = `Request failed (${res.status})`;
        try {
          const data = await res.json();
          msg = data?.message || data?.error || msg;
        } catch {}
        return rejectWithValue(msg);
      }

      const data = await res.json();
      return data; // keep entire payload
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch products");
    }
  }
);

const initialState = {
  items: [],
  status: "idle", // idle | loading | succeeded | failed
  error: "",
  page: 1,
  limit: 10,
  total: 0,
  totalPages: 1,
  filters: {
    category: null,
    inStock: true,
  },
};

const productsSlice = createSlice({
  name: "products",
  initialState,
  reducers: {
    setProductsFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      // when filters change, reset to page 1
      state.page = 1;
    },
    setProductsPage: (state, action) => {
      state.page = action.payload;
    },
    setProductsLimit: (state, action) => {
      state.limit = action.payload;
      state.page = 1;
    },
    clearProductsError: (state) => {
      state.error = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProductsWithFilter.pending, (state) => {
        state.status = "loading";
        state.error = "";
      })
      .addCase(fetchProductsWithFilter.fulfilled, (state, action) => {
        state.status = "succeeded";

        const payload = action.payload;
        const d = payload?.data || {};

        state.page = d.page ?? state.page;
        state.limit = d.limit ?? state.limit;
        state.total = d.total ?? 0;
        state.totalPages = d.totalPages ?? 1;
        state.items = Array.isArray(d.products) ? d.products : [];
      })
      .addCase(fetchProductsWithFilter.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch products";
        state.items = [];
      });
  },
});

export const {
  setProductsFilters,
  setProductsPage,
  setProductsLimit,
  clearProductsError,
} = productsSlice.actions;

export default productsSlice.reducer;

// selectors
export const selectProducts = (s) => s.products.items;
export const selectProductsStatus = (s) => s.products.status;
export const selectProductsError = (s) => s.products.error;
export const selectProductsMeta = (s) => ({
  page: s.products.page,
  limit: s.products.limit,
  total: s.products.total,
  totalPages: s.products.totalPages,
});
export const selectProductsFilters = (s) => s.products.filters;
